import { Stack, Typography } from "@mui/material";
import React, { useContext } from "react";
import CardActionMenu from "src/components/UI/CardActionMenu";
import { AlenSchedule, frequencyDays } from "src/interfaces/IProgram";
import {
  BORDER_COLOR_CARD,
  DM_COLOR_ACCENT,
  INACTIVE_RED,
  MESSAGING_SUCCESS,
  MESSAGING_WARNING,
} from "src/styles";
import { updateAlenSchedule } from "src/services/alen/useAlenSchedules";
import { AuthContext } from "src/contexts/AuthContext";
import { FrequencyLabel } from "./NewSchedulingDetailsDialog/FrequencySelector";
import { UpdateScheduleStatusDto } from "./types";

function Tag({ label, color }: { label: string; color: string }) {
  return (
    <Typography
      variant="subtitle4"
      sx={{
        position: "absolute",
        top: "0",
        right: "0",
        lineHeight: "1rem",
        paddingX: ".5rem",
        borderRadius: "4px",
        backgroundColor: color,
        marginLeft: ".5rem",
        color: "white",
      }}
    >
      {label}
    </Typography>
  );
}
function ScheduleItem({
  schedule,
  onStatusChange,
  onClick,
}: {
  schedule: AlenSchedule;
  onStatusChange: () => void;
  onClick?: () => void;
}) {
  const {
    deviceSchedulerId,
    actionCode,
    actionStartValue,
    nextOccurrenceLabel,
    nextOccurrenceValue,
    schedulerName,
    timezone,
    enabled,
    cancelNextRun,
  } = schedule;

  const { user } = useContext(AuthContext);

  const handleStatusUpdate = async (
    changes: Partial<UpdateScheduleStatusDto>
  ) => {
    if (!user?.alen) return;
    await updateAlenSchedule(user.alen, {
      deviceSchedulerId,
      enabled,
      cancelNextRun,
      deleted: false,
      ...changes,
    });
    onStatusChange();
  };
  return (
    <Stack
      sx={{
        borderRadius: "4px",
        width: "408px",
        border: 1,
        borderColor: BORDER_COLOR_CARD,
        backgroundColor: "white",
        ...(onClick
          ? {
              ":hover": { borderColor: DM_COLOR_ACCENT },
            }
          : {}),
      }}
      direction="row"
    >
      <Stack
        onClick={onClick}
        sx={{
          padding: "1rem",
          gap: ".5rem",
          position: "relative",
          flex: 1,
          cursor: onClick ? "pointer" : "default",
        }}
      >
        {!enabled && <Tag label="Disabled" color={INACTIVE_RED} />}
        {enabled && cancelNextRun && (
          <Tag label="Next Run Cancelled" color={MESSAGING_WARNING} />
        )}
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2" flex={1}>
            {schedulerName}
          </Typography>
        </Stack>
        <Typography variant="body2">
          {`Set ${actionCode}: ${actionStartValue} at ${schedule.actionStartTime} ${timezone}`}
        </Typography>
        {schedule.actionEndValue ? (
          <Typography variant="body2">
            {`Set ${actionCode}: ${schedule.actionEndValue} at ${schedule.actionEndTime} ${timezone}`}
          </Typography>
        ) : null}
        <Stack direction="row" gap=".25rem">
          {frequencyDays.map(({ label, value }) => (
            <FrequencyLabel
              key={value}
              selected={schedule.frequency.days.includes(value)}
              label={label}
            />
          ))}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          gap={1}
        >
          <Typography variant="subtitle4">{nextOccurrenceLabel}</Typography>
          <Typography
            variant="subtitle4"
            sx={{
              lineHeight: "1rem",
              paddingX: ".5rem",
              borderRadius: "4px",
              backgroundColor: MESSAGING_SUCCESS,
              color: "white",
            }}
          >
            {nextOccurrenceValue}
          </Typography>
        </Stack>
      </Stack>
      <CardActionMenu
        options={[
          {
            label: enabled ? "Deactivate" : "Reactivate",
            onClick: () => {
              handleStatusUpdate({ enabled: !enabled });
            },
          },
          {
            label: cancelNextRun ? "Resume Next Run" : "Cancel Next Run",
            onClick: () => {
              handleStatusUpdate({ cancelNextRun: !cancelNextRun });
            },
          },
          {
            label: "Delete",
            onClick: () => {
              handleStatusUpdate({ deleted: true });
            },
          },
        ]}
      />
    </Stack>
  );
}
export default ScheduleItem;
